import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { constants } from '@yola/subscription-manager-js';
import prepareSummaryData from './helpers/prepare-summary-data';
import prepareData from './helpers/prepare-data';
import prepareAndEncodeData from './helpers/prepare-and-encode-data';
import prepareTraitsData from './helpers/prepare-traits-data';
import getFields from './helpers/get-fields';
import useSetupSteps from './hooks/use-setup-steps';
import useSetupForm from './hooks/use-setup-form';
import setupSteps from './constants/setup-steps';
import { serviceValues } from './constants/fields-values';
import fieldNames from './constants/field-names';
import DifmOrderFormDialog from './components/difm-order-form-dialog';
import { t } from '../../localization';
import useMatchBreakpoint from '../../common/hooks/use-match-breakpoint';
import breakpoints from '../../common/constants/breakpoints';
import generatePaymentUrl from '../../utils/generate-payment-url';

const {
  SubscriptionType: { B2C_HOSTING_GOLD },
  SubscriptionTerm: { MONTHLY },
} = constants;

const getCaptions = (service, { isPromoFlow }) => {
  const detailsCaptions = {
    [serviceValues.BUILD_NEW_WEBSITE]: {
      sectionTitle: t('Help us understand your needs'),
      sectionDescription: t(
        'Answer a couple of questions to help us build the right website for you'
      ),
    },
    [serviceValues.REVAMP_EXISTING_WEBSITE]: {
      sectionTitle: t('Help us understand your needs'),
      sectionDescription: t('Answer a couple of questions to help us make the right edits for you'),
    },
    [serviceValues.MIGRATE_WEBSITE]: {
      sectionTitle: t('Help us understand your needs'),
      sectionDescription: t(
        'Answer a couple of questions to help us build the right website for you'
      ),
    },
  }[service];

  return {
    backButtonLabel: t('Back'),
    nextButtonLabel: t('Continue'),
    submitButtonLabel: isPromoFlow ? t('Submit & Schedule a Call') : t('Proceed to checkout'),
    stepsPanel: {
      title: t('Professional website services'),
      description: t('Build and maintain your site with our team of design experts'),
      steps: {
        [setupSteps.PROJECT]: t('Select a project'),
        [setupSteps.DETAILS]: t('Provide details'),
        [setupSteps.CONTACT_INFO]: t('Contact information'),
        [setupSteps.SUMMARY]: t('Summary'),
      },
    },
    [setupSteps.PROJECT]: {
      sectionTitle: t('What would you like us to do?'),
      sectionDescription: t(
        'Choose the option that best describes your current needs. We’ll guide you through the rest'
      ),
    },
    [setupSteps.DETAILS]: detailsCaptions,
    [setupSteps.CONTACT_INFO]: {
      sectionTitle: t('Contact Information'),
      sectionDescription: t('We’ll use this to get in touch and start your project.'),
    },
    [setupSteps.SUMMARY]: {
      sectionTitle: t('Summary'),
      sectionDescription: t(`Please review your project information`),
      editButtonLabel: t('Edit'),
      showDescriptionTitle: t('Show more...'),
      collapseDescriptionTitle: t('Collapse'),
    },
  };
};

const DifmOrderFormContainer = ({
  initialValues = {},
  details = {},
  initiator = '',
  term = '',
  isMobile = false,
  isPromoFlow = false,
  onClose,
  onSubmit,
  onNext,
  onBack,
  onDisplay,
  onChange,
}) => {
  const fields = getFields();

  const {
    steps,
    activeStepId,
    isFirstStep,
    isLastStep,
    resetSteps,
    onChangeStep,
    onPrevStep,
    onNextStep,
  } = useSetupSteps();
  const {
    control,
    getValues,
    triggerValidation,
    setFocus,
    activeStepFieldIds,
    activeStepFieldNames,
  } = useSetupForm({ activeStepId, fields, resetSteps, initialValues });

  const isMatchDesktop = useMatchBreakpoint(breakpoints.MD);
  const scrollableContainerRef = useRef(null);
  const fieldToFocus = useRef(null);

  const captions = getCaptions(getValues(fieldNames.SERVICE), { isPromoFlow });

  const summaryData =
    activeStepId === setupSteps.SUMMARY ? prepareSummaryData(getValues(), fields) : [];
  const isDesktopView = !isMobile && isMatchDesktop;

  const handleBack = async () => {
    const isValid = await triggerValidation();

    onBack({ stepId: activeStepId });
    onPrevStep({
      markStepAsCompleted: isValid,
      markStepAsInvalid: !isValid,
    });
  };

  const handleNext = async () => {
    const isValid = await triggerValidation();

    onNext({
      stepId: activeStepId,
    });
    onNextStep({
      markStepAsCompleted: isValid,
      markStepAsInvalid: !isValid,
    });
  };

  const handleChange = async (stepId) => {
    const isValid = await triggerValidation();

    onChange({ stepId: activeStepId, targetStepId: stepId });
    onChangeStep(stepId, {
      markStepAsCompleted: isValid,
      markStepAsInvalid: !isValid,
    });
  };

  const handleEdit = (stepId, fieldId) => {
    const fieldName = fields.find((field) => field.id === fieldId)?.name;
    fieldToFocus.current = fieldName;

    onChange({ stepId: activeStepId, targetStepId: stepId });
    onChangeStep(stepId);
  };

  const handleClose = () => {
    onClose({ stepId: activeStepId });
  };

  const handleSubmit = () => {
    const values = getValues();
    const fieldData = prepareData(values, fields);
    const promoCampaignDetails = prepareAndEncodeData(fieldData, details);
    const traitsData = prepareTraitsData(fieldData);
    const paymentData = {
      products: [B2C_HOSTING_GOLD],
      term: term || MONTHLY,
      promo_campaign_details: promoCampaignDetails,
      ...(initiator && { initiator }),
    };
    const paymentUrl = generatePaymentUrl(paymentData);

    onSubmit({
      fieldData,
      traitsData,
      paymentUrl,
      paymentData,
    });
  };

  useLayoutEffect(() => {
    scrollableContainerRef.current.scrollTop = 0;

    if (fieldToFocus.current) {
      setFocus(fieldToFocus.current);
      fieldToFocus.current = null;
    }

    onDisplay({ stepId: activeStepId });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [activeStepId]);

  return (
    <DifmOrderFormDialog
      scrollableContainerRef={scrollableContainerRef}
      summaryData={summaryData}
      activeStepId={activeStepId}
      control={control}
      captions={captions}
      steps={steps}
      activeStepFieldIds={activeStepFieldIds}
      activeStepFieldNames={activeStepFieldNames}
      fields={fields}
      isDesktopView={isDesktopView}
      showBackButton={!isFirstStep}
      showSubmitButton={isLastStep}
      getValues={getValues}
      onBack={handleBack}
      onNext={handleNext}
      onEdit={handleEdit}
      onChange={handleChange}
      onClose={handleClose}
      onSubmit={handleSubmit}
    />
  );
};

DifmOrderFormContainer.propTypes = {
  initialValues: PropTypes.shape({
    userFullName: PropTypes.string,
    userEmail: PropTypes.string,
  }),
  details: PropTypes.shape({
    domainName: PropTypes.string,
    businessEmail: PropTypes.string,
    businessPhone: PropTypes.string,
    businessAddress: PropTypes.string,
    businessHours: PropTypes.string,
  }),
  term: PropTypes.string,
  initiator: PropTypes.string,
  isMobile: PropTypes.bool,
  isPromoFlow: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onDisplay: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DifmOrderFormContainer;
